<template>
  <Application>
    <b-breadcrumb :items="items"></b-breadcrumb>

    <div
      class="d-flex justify-content-center py-4"
      v-if="isLoadingProject"
    >
      <b-spinner variant="secondary"></b-spinner>
    </div>

    <div v-else>
      <b-row class="mt-3">
        <b-col md="2">
          <div class="shadow-sm rounded bg-white block mb-2 mb-md-0">
            <h2 class="mb-0 px-2 py-1">{{count.total}}</h2>
            <div class="block__footer px-2">
              <small class="text-muted">Tarefas ao total</small>
            </div>
          </div>
        </b-col>
        <b-col md="2">
          <div class="shadow-sm rounded bg-white block block--unknown mb-4 mb-md-0">
            <h2 class="mb-0 px-2 py-1">{{count.unknown}}</h2>
            <div class="block__footer px-2">
              <small class="text-muted">Tarefas sem data</small>
            </div>
          </div>
        </b-col>
        <b-col md="2">
          <div class="shadow-sm rounded bg-white block block--past mb-2 mb-md-0">
            <h2 class="mb-0 px-2 py-1">{{count.delayed}}</h2>
            <div class="block__footer px-2">
              <small class="text-muted">Tarefas atrasadas</small>
            </div>
          </div>
        </b-col>
        <b-col md="2">
          <div class="shadow-sm rounded bg-white block block--today mb-2 mb-md-0">
            <h2 class="mb-0 px-2 py-1">{{count.today}}</h2>

            <div class="block__footer px-2">
              <small class="text-muted">Tarefas para hoje</small>
            </div>
          </div>
        </b-col>
        <b-col md="2">
          <div class="shadow-sm rounded bg-white block block--future mb-2 mb-md-0">
            <h2 class="mb-0 px-2 py-1">{{count.future}}</h2>
            <div class="block__footer px-2">
              <small class="text-muted">Tarefas próximos dias</small>
            </div>
          </div>
        </b-col>
        <b-col md="2" class="d-flex align-items-center justify-content-center">
          <router-link to="/projects/leads">
            <div
              class="text-secondary d-flex flex-column align-items-center justify-content-center"
            >
              <b-icon-arrow-right-circle class="mb-2 h4"/>
              <small>Ver projeto</small>
            </div>
          </router-link>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <b-card>
            <template v-slot:header>
              <div class="d-flexalign-items-center">
                <span>Resumo de tarefas por corretor</span>

                <small class="text-center ml-2">
                  <b-icon-question-circle-fill
                    v-b-popover.hover="taskMessage"
                    class="text-info"
                  />
                </small>
              </div>
            </template>
            <b-table
              small
              striped
              bordered
              responsive="lg"
              class="mb-0"
              :fields="fields"
              :items="tasksSummary"
              :per-page="perPage"
              :current-page="currentPage"
            >
              <template v-slot:table-busy>
                <div class="d-flex align-items-center justify-content-center p-4">
                  <b-spinner></b-spinner>
                  <small class="ml-2 font-weight-bold">Carregando dados</small>
                </div>
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col>
          <b-pagination
            class="mb-0"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
          />
        </b-col>
      </b-row>
    </div>
  </Application>
</template>

<script>
import { ProjectsService } from '@/services';
import Application from '@/components/Application/Application.vue';

export default {
  components: {
    Application,
  },

  data() {
    return {
      items: [
        { text: 'Dashboard', active: true },
      ],
      taskMessage: 'Ordenado por quantidade de tarefas. (Do corretor mais atarefado ao menos atarefado)',
      isLoadingProject: false,
      project: {},
      currentPage: 1,
      perPage: 10,
      fields: [
        { key: 'assignee', label: 'Corretor' },
        { key: 'total', label: 'Tarefas', class: 'text-center' },
        { key: 'unknown', label: 'Sem data', class: 'text-center' },
        { key: 'delayed', label: 'Atrasadas', class: 'text-center' },
        { key: 'today', label: 'Para hoje', class: 'text-center' },
        { key: 'future', label: 'Próximos dias', class: 'text-center' },
      ],
    };
  },

  async mounted() {
    if (!this.isAdmin) {
      this.$router.push('/sales-funnel');
    } else {
      this.isLoadingProject = true;
      this.project = await ProjectsService.fetchDefaultProjectTasks(this.user.company_id);
      this.isLoadingProject = false;
    }
  },

  computed: {
    tasksSummary() {
      if (!this.project.tasks) {
        return [];
      }

      const brokers = [...new Set(this.project.tasks.map((task) => task.assignee_id))];

      return brokers
        .map((assigneeId) => {
          const tasks = this.getBrokerTasksByAssigneeId(assigneeId);

          return {
            assignee: tasks[0].assignee,
            total: tasks.length,
            unknown: this.getTaskCountBySituation(tasks, 'unknown'),
            delayed: this.getTaskCountBySituation(tasks, 'past'),
            today: this.getTaskCountBySituation(tasks, 'today'),
            future: this.getTaskCountBySituation(tasks, 'future'),
          };
        })
        .sort((curr, next) => (curr.total < next.total ? 1 : -1));
    },

    count() {
      if (!this.project.tasks) {
        return {};
      }

      const { tasks } = this.project;

      return {
        total: tasks.length,
        delayed: this.getTaskCountBySituation(tasks, 'past'),
        today: this.getTaskCountBySituation(tasks, 'today'),
        future: this.getTaskCountBySituation(tasks, 'future'),
        unknown: this.getTaskCountBySituation(tasks, 'unknown'),
      };
    },

    rows() {
      return (this.tasksSummary)
        ? this.tasksSummary.length
        : 0;
    },
  },

  methods: {
    getBrokerFullName(assigneeId) {
      return this.project.tasks.find((task) => task.assignee_id === assigneeId).name;
    },

    getBrokerTasksByAssigneeId(assigneeId) {
      return this.project.tasks.filter((task) => task.assignee_id === assigneeId);
    },

    getTaskCountBySituation(tasks, situation) {
      const count = tasks.filter((task) => task.situation === situation).length;

      return count || '-';
    },
  },
};
</script>

<style lang="scss" scoped>
  .block {
    &--unknown {
      border-left: 4px solid #6c757d;
    }

    &--past {
      border-left: 4px solid #dc3545;
    }

    &--today {
      border-left: 4px solid #28a745;
    }

    &--future {
      border-left: 4px solid #007bff;
    }

    &__footer {
      align-items: center;
      background: rgba(249, 249, 249, .8);
      border-top: 1px solid rgba(0, 0, 0, .125);
      display: flex;
      font-size: .875em;
      justify-content: space-between;

      .assignee {
        display: inline-block;
        max-width: 50%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
</style>
